const FACEBOOK_FINISH_EVENTS = new Set(["FINISH", "FINISH_ONLY_WABA", "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING"]);
const FACEBOOK_ENDED_EVENTS = new Set(["CANCEL", "ERROR"]);

type EmbeddedSignupPayload = {
  type?: unknown;
  event?: unknown;
  data?: {
    waba_id?: unknown;
    business_id?: unknown;
  };
};

export type EmbeddedSignupSession = {
  wabaId: string;
  businessId?: string;
};

export type EmbeddedSignupEvent = { type: "finish"; wabaId: string; businessId?: string } | { type: "ended" };

export const EMBEDDED_SIGNUP_LOGIN_GRACE_MS = 10_000;

function isFacebookOrigin(origin: string): boolean {
  try {
    const url = new URL(origin);
    return url.protocol === "https:" && (url.hostname === "facebook.com" || url.hostname.endsWith(".facebook.com"));
  } catch {
    return false;
  }
}

function isEmbeddedSignupPayload(value: unknown): value is EmbeddedSignupPayload {
  return typeof value === "object" && value !== null;
}

function parsePayload(data: unknown): EmbeddedSignupPayload | undefined {
  if (isEmbeddedSignupPayload(data)) {
    return data;
  }
  if (typeof data !== "string") {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(data);
    return isEmbeddedSignupPayload(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function optionalId(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

export function parseEmbeddedSignupEvent(origin: string, data: unknown): EmbeddedSignupEvent | undefined {
  if (!isFacebookOrigin(origin)) {
    return undefined;
  }
  const payload = parsePayload(data);
  if (!payload || payload.type !== "WA_EMBEDDED_SIGNUP") {
    return undefined;
  }
  const eventName = String(payload.event ?? "").toUpperCase();
  if (FACEBOOK_ENDED_EVENTS.has(eventName)) {
    return { type: "ended" };
  }
  if (!FACEBOOK_FINISH_EVENTS.has(eventName)) {
    return undefined;
  }
  const wabaId = optionalId(payload.data?.waba_id);
  if (!wabaId) {
    return undefined;
  }
  const businessId = optionalId(payload.data?.business_id);
  return businessId ? { type: "finish", wabaId, businessId } : { type: "finish", wabaId };
}

export function listenForEmbeddedSignupSession(target: EventTarget = window): {
  session: Promise<EmbeddedSignupSession | undefined>;
  stop: () => void;
} {
  let settled = false;
  let resolveSession: (value: EmbeddedSignupSession | undefined) => void = () => undefined;
  const session = new Promise<EmbeddedSignupSession | undefined>((resolve) => {
    resolveSession = resolve;
  });

  const onMessage = (event: Event) => {
    if (!(event instanceof MessageEvent)) {
      return;
    }
    const parsed = parseEmbeddedSignupEvent(event.origin, event.data);
    if (!parsed || settled) {
      return;
    }
    settled = true;
    if (parsed.type !== "finish") {
      resolveSession(undefined);
      return;
    }
    resolveSession(parsed.businessId ? { wabaId: parsed.wabaId, businessId: parsed.businessId } : { wabaId: parsed.wabaId });
  };

  target.addEventListener("message", onMessage);

  return {
    session,
    stop: () => {
      target.removeEventListener("message", onMessage);
      if (!settled) {
        settled = true;
        resolveSession(undefined);
      }
    },
  };
}
